/**
 * The action's resolved gate state, passed to `extra` so query siblings can gate
 * themselves (`gateEnabled(isAuthorized, …)`) and nested actions can forward the
 * resolved context.
 */
export interface GateResolved<TSubject> {
  readonly isAuthorized: boolean;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly subject: TSubject | null;
}

export interface GatedActionConfig<
  TDeps,
  TSubject,
  TBundle extends object,
  TExtra extends object,
> {
  /** Instantiate every DAL hook unconditionally (Rules of Hooks). */
  readonly useDeps: () => TDeps;
  readonly isLoading: (deps: TDeps) => boolean;
  /**
   * The action's authorization context failed to load (network/500 fetching the
   * source that carries `$roles`). This is NOT authorization denial — denial is a
   * `null` bundle. Reserved for genuine load failures.
   */
  readonly error: (deps: TDeps) => Error | null;
  /**
   * Resolve the authorized entity from the caller's `$roles`, or `null` when the
   * caller is not authorized. A non-null result IS the authorization and is
   * surfaced as `subject`.
   */
  readonly authorize: (deps: TDeps) => TSubject | null;
  /**
   * Assemble the gated mutation bundle from the already-instantiated deps. Omit
   * for actions that have no mutations — `bundle` is then always `null`.
   */
  readonly bundle?: (deps: TDeps, subject: TSubject) => TBundle;
  /** Build the query siblings; gate each with `gateEnabled(resolved.isAuthorized, …)`. */
  readonly extra: (deps: TDeps, resolved: GateResolved<TSubject>) => TExtra;
}

/**
 * Template-method orchestrator for a gated actions hook. Owns the shape every
 * `use*Actions` gate repeats — instantiate deps, resolve loading, resolve the
 * authorized entity, build the nullable bundle, expose query siblings — so each
 * implementer only declares the slots. `subject` is the authorized entity (or
 * `null`); `bundle` is `null` while loading or unauthorized (a non-null bundle IS
 * the authorization). `useDeps` runs unconditionally at the top so Rules of Hooks
 * hold.
 */
export function useGatedAction<
  TDeps,
  TSubject,
  TExtra extends object,
  TBundle extends object = Record<string, never>,
>(
  config: GatedActionConfig<TDeps, TSubject, TBundle, TExtra>,
): {
  isLoading: boolean;
  error: Error | null;
  subject: TSubject | null;
  bundle: TBundle | null;
} & TExtra {
  const deps = config.useDeps();
  const isLoading = config.isLoading(deps);
  const error = config.error(deps);
  const subject = config.authorize(deps);
  const bundle: TBundle | null =
    config.bundle !== undefined && subject !== null && !isLoading
      ? config.bundle(deps, subject)
      : null;
  const extra = config.extra(deps, {
    isAuthorized: subject !== null,
    isLoading,
    error,
    subject,
  });
  return { isLoading, error, subject, bundle, ...extra };
}
